// Creates an LRU cache for blob object URLs keyed by an arbitrary string.
// Shared by utils that resolve a media blob URL reactively (see
// utils/avatar-url.js, utils/media-image-url.js, utils/media-url.js). Capped
// to avoid pinning unbounded blob memory -- each entry holds a
// createObjectURL reference until it is revoked on eviction.
export interface BlobUrlCache {
  get(key: string): string | null;
  set(key: string, url: string): void;
  clear(): void;
}

// TS-BUG: A maxSize below 1 makes set() throw. It evicts from an empty Map.
export function createBlobUrlCache(maxSize = 2000): BlobUrlCache {
  const cache = new Map<string, string>();

  return {
    get(key) {
      if (!cache.has(key)) return null;
      const url = cache.get(key) as string; // has(key) checked above.
      // Promote to most-recently-used by reinserting at the end of the Map.
      cache.delete(key);
      cache.set(key, url);
      return url;
    },

    set(key, url) {
      if (cache.size >= maxSize) {
        // Evict the least-recently-used entry (first in insertion order) and
        // release its blob memory immediately.
        // Non-empty here, as size >= maxSize.
        const [oldestKey, oldestUrl] = cache.entries().next().value as [
          string,
          string,
        ];
        URL.revokeObjectURL(oldestUrl);
        cache.delete(oldestKey);
      }
      cache.set(key, url);
    },

    clear() {
      for (const url of cache.values()) {
        URL.revokeObjectURL(url);
      }
      cache.clear();
    },
  };
}
